<template>
    <v-dialog
        v-model="show"
        content-class="cod-confirm-dialog"
        :max-width="width"
        :persistent="persistent"
        @keydown.esc="choose(null)"
    >
        <v-toolbar v-if="Boolean(title)" :color="color" dark>
            <v-btn
                icon
                dark
                v-if="Boolean(icon)"
                @focus="$event.target.blur()"
                @click="choose(null)"
            >
                <v-icon class="icon mr-0">
                    {{ icon }}
                </v-icon>
            </v-btn>
            <v-toolbar-title class="white--text">{{ title }}</v-toolbar-title>
        </v-toolbar>

        <v-card class="msg-container" tile>
            <v-card-text class="message">
                <div v-html="message"></div>

                <template v-if="renderFields">
                    <template v-for="(field, index) in formFields">
                        <v-autocomplete
                            v-if="field.type === 'select'"
                            :key="`select${index}`"
                            v-model="values[field.key]"
                            :class="{ 'mt-3': index === 0, 'my-0': index > 0 }"
                            :items="field.items"
                            :filter="filter"
                            :item-text="field.itemText"
                            :item-key="field.itemKey || undefined"
                            :label="field.label"
                            clear-icon="fal fa-times"
                            :return-object="!field.itemKey"
                            auto-select-first
                            clearable
                            @change="onChange($event, field)"
                        />

                        <v-text-field
                            v-else-if="field.type === 'text'"
                            :key="`text${index}`"
                            v-model="values[field.key]"
                            v-mask="field.mask"
                            :class="{ 'mt-3': index === 0, 'my-0': index > 0 }"
                            :label="field.label"
                            :hint="field.hint"
                            @change="onChange($event, field)"
                        />

                        <v-checkbox
                            v-else-if="field.type === 'checkbox'"
                            :key="`checkbox${index}`"
                            v-model="values[field.key]"
                            class="mt-3"
                            :label="field.label"
                            hide-details
                            @change="onChange($event, field)"
                        />

                        <v-file-input
                            v-else-if="field.type === 'file'"
                            :key="`file${index}`"
                            :label="field.label"
                            clearable
                            multiple
                            prepend-icon="fal fa-paperclip"
                            clear-icon="fal fa-times"
                            @change="onChange($event, field)"
                        />
                    </template>
                </template>
            </v-card-text>

            <v-card-actions>
                <v-btn
                    v-if="buttonCancelText"
                    :color="buttonCancelColor"
                    text
                    class="minw100"
                    :disabled="!show"
                    @click="choose(null)"
                >
                    {{ buttonCancelText }}
                </v-btn>
                <v-spacer v-if="buttonCancelText" />
                <v-btn
                    v-if="buttonFalseText"
                    :color="buttonFalseColor"
                    text
                    class="minw100"
                    :disabled="!show"
                    @click="choose(false)"
                >
                    {{ buttonFalseText }}
                </v-btn>
                <v-spacer v-if="!buttonCancelText" />
                <v-btn
                    v-if="buttonTrueText"
                    :color="buttonTrueColor"
                    text
                    class="minw100"
                    :disabled="disableButtonTrue"
                    @click="choose(true)"
                >
                    {{ buttonTrueText }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    created() {
        this.eventBus.$on('show-confirm', this.showConfirm);
    },

    beforeDestroy() {
        this.eventBus.$off('show-confirm', this.showConfirm);
    },

    data() {
        return {
            show: false,
            buttonCancelText: 'Cancel',
            buttonTrueText: 'Yes',
            buttonFalseText: 'No',
            buttonCancelColor: 'grey',
            buttonTrueColor: 'primary',
            buttonFalseColor: 'grey',
            color: 'warning',
            icon: 'far fa-exclamation-circle',
            message: null,
            persistent: false,
            title: null,
            width: 350,
            formFields: [],
            values: {},
            renderFields: false,
            callback: () => {}
        };
    },

    computed: {
        disableButtonTrue() {
            if(!this.show) return true;

            if(!this.formFields?.length) return false;

            let disabled = false;

            this.formFields.forEach(field => {
                const validations = field.validation?.split('|') || []
                for (const validation of validations) {
                    if(validation === 'required' && !this.values[field.key]) {
                        disabled = true;
                    }

                    if(validation?.startsWith('required_if')) {
                        const segments = validation.split(':');
                        if(segments.length > 1) {
                            const key = segments[1].split(',')[0]
                            const value = segments[1].split(',')[1]
                            if(!this.values[field.key] && this.values[key] === value) {
                                disabled = true;
                            }
                        }
                    }

                    if(validation?.startsWith('required_without_all')) {
                        const segments = validation.split(':');

                        if(segments.length > 1) {
                            const withoutFields = segments[1].split(',').filter(key => this.formFields.some(f => f.key === key))
                            const noWithoutFieldsHaveValue = !withoutFields.some(key => !!this.values[key]);
                            if(noWithoutFieldsHaveValue && !this.values[field.key]) {
                                disabled = true;
                            }
                        }
                    }

                    if(validation?.startsWith('date') && this.values[field.key]) {
                        const pattern = validation.split(':')[1]
                        if(!moment(this.values[field.key], pattern).isValid()) {
                            disabled = true;
                        }
                    }
                }
            });

            return disabled;
        }
    },

    methods: {
        showConfirm(event) {
            const message = event.message
            const options = event.options || {}
            const callback = event.callback

            let delay = this.show ? 500 : 0;
            this.show = false;
            this.formFields = [];
            this.values = {};
            this.renderFields = false;
            this.width = 350;

            setTimeout(() => {
                this.buttonTrueText = options.buttonTrueText || null;
                this.buttonFalseText = options.buttonFalseText || null;
                this.buttonCancelText = options.buttonCancelText || null;
                this.buttonTrueColor = options.buttonTrueColor || 'primary';
                this.buttonFalseColor = options.buttonFalseColor || 'grey';
                this.buttonCancelColor = options.buttonCancelColor || 'grey';
                this.color = options.color || 'warning';
                this.icon = options.icon;
                this.message = message;
                this.persistent = options.persistent || false;
                this.title = options.title;
                this.width = options.width || 350;
                this.callback = callback
                    ? result => {
                          this.show = false;
                          callback(result);
                      }
                    : result => (this.show = false);

                this.formFields = options.formFields;
                if(options.formFields?.length) {
                    options.formFields.forEach(field => {
                        this.$set(this.values, field.key, field.default || null);
                    });
                }

                this.show = true;
                this.renderFields = this.formFields?.length > 0;
            }, delay);
        },

        choose(result) {
            if(Object.keys(this.values).length) {
                return this.callback({ confirm: result, data: this.values });
            }

            this.callback(result);
        },

        filter(item, queryText, itemText) {
            const selectFieldWithSearchBy = this.formFields.find(f => f.searchBy)

            if(!selectFieldWithSearchBy) return false;

            return selectFieldWithSearchBy.searchBy
                .some(field => item[field].toLowerCase()
                .indexOf(queryText.toLowerCase()) > -1);
        },

        async onChange($event, field) {
            if(field.type === 'file') {
                await this.encodeFiles($event, field.key)
            }

            if(field.onChange) {
                field.onChange($event, this.values)
            }
        },

        async encodeFiles(files, key) {
            this.values[key] = []

            if(!files || !files.length) return;

            files.forEach(async file => {
                const base64 = await Form.fileToBase64(file);
                this.values[key].push({
                    name: file.name,
                    type: file.type,
                    data: base64
                });
            });
        }
    }
};
</script>

<style lang="scss">
.cod-confirm-dialog {
    // overflow-y: hidden;
}
</style>

<style lang="scss" scoped>
.icon {
    margin-right: 10px;
}

.msg-container {
    .message {
        font-size: 16px;
        padding: 20px !important;
        color: rgba(0, 0, 0, 0.8) !important;
    }
}
</style>
